import { useEffect, useState } from 'react';

/** Sticky mobile section-nav — horizontally-scrollable chips that smooth-scroll
 *  to a section and track the one in view (scrollspy). Mobile-only; all sections
 *  stay in the DOM (anchor jumps, not a tab switcher) so nothing is hidden from
 *  crawlers. Renders nothing when there are fewer than two navigable sections.
 *
 *  Each target section needs the matching `id` and a `scroll-mt-*` so the smooth
 *  scroll doesn't tuck its heading under this bar. Shared by the tender-detail
 *  and buyer-profile pages. */
export function MobileSectionNav({ sections }: { sections: { id: string; label: string }[] }) {
  const [active, setActive] = useState(sections[0]?.id);

  useEffect(() => {
    const els = sections
      .map((s) => document.getElementById(s.id))
      .filter((el): el is HTMLElement => el != null);
    if (els.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const inView = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (inView[0]) setActive(inView[0].target.id);
      },
      { rootMargin: '-44% 0px -50% 0px' },
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [sections]);

  if (sections.length < 2) return null;
  // `-top-5` (−20px) is what pins this bar flush under the header, and it must
  // stay: a sticky element's stuck position is set by its `top` inset ALONE, and
  // `main` (the scroll container) has `pt-5` (20px) — so `top:-20px` exactly
  // cancels that, landing the box at the header with no gap. Raising `top` toward
  // 0 reopens the gap and bleeds scrolling page content into the strip above the
  // bar (measured). The top PADDING is independent of all that — purely visual
  // space above the pills — so it's small and symmetric with the bottom here;
  // tighten density by changing the padding, never the `top`.
  return (
    <nav className="sticky -top-5 z-20 -mx-4 border-b border-edge bg-bone-surface px-4 py-2.5 shadow-[0_2px_6px_-4px_rgba(20,20,20,0.15)] lg:hidden">
      <div className="flex gap-2 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {sections.map((s) => (
          <a
            key={s.id}
            href={`#${s.id}`}
            onClick={(e) => {
              e.preventDefault();
              document.getElementById(s.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              setActive(s.id);
            }}
            className={`inline-flex shrink-0 items-center rounded-pill px-3.5 py-2 text-[13px] font-semibold leading-none transition-colors ${
              active === s.id ? 'bg-brand text-white' : 'bg-bone text-ink-soft hover:bg-bone-hover'
            }`}
          >
            {s.label}
          </a>
        ))}
      </div>
    </nav>
  );
}
